import React, { Fragment, useMemo } from "react";
import LBox from "@components/LBox";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import BackButton from "@components/TopBar/BackButton";
import InboxButton from "@components/TopBar/InboxButton";
import { useRouter } from "expo-router";
import { getTopBarOptions } from "@components/TopBar/helpers";
import { useTopBarHeight } from "@hooks/useTopBarHeight";
import { BUTTONS } from "@components/TopBar/constants";
import PageTitle from "@components/TopBar/PageTitle";
import usePrevious from "@hooks/usePrevious";
import { Platform } from "react-native";
import ViewAppButton from "@components/TopBar/ViewAppButton";
import { Easing, useAnimatedStyle, withTiming } from "react-native-reanimated";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import type { NavigationState } from "@utils/navigation/types";
import { useFarcasterContext } from "@hooks/useFarcasterContext";

const noBackButtonRoutes = [
  "/explore",
  "/explore/access",
  "/experiences",
  "/library",
  "/inbox",
  "/profile/wallet",
  "/profile/you",
  "includes:/profile/artist",
];

const allowBackButton = (pathname: string) => {
  for (const route of noBackButtonRoutes) {
    if (route.startsWith("includes:")) {
      const subRoute = route.slice("includes:".length);
      if (subRoute && pathname.includes(subRoute)) {
        return false;
      }
    } else {
      if (pathname === route) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Helper function to determine back button state for web platforms.
 * Extracted for clarity and to handle the Farcaster loading state gracefully.
 */
const getWebBackButtonState = (
  isInMiniApp: boolean | null,
  pathHistory: string[],
  router: ReturnType<typeof useRouter>,
): boolean => {
  // In Farcaster Mini App: use pathHistory since window.location.replace doesn't create history
  if (isInMiniApp === true) {
    // pathHistory always starts with ["/"], so we need more than 1 entry
    return pathHistory.length > 1;
  }

  // Still loading Farcaster context: use pathHistory to avoid flicker
  // This works for both Mini App and regular web during the brief loading period
  if (isInMiniApp === null) {
    return pathHistory.length > 1;
  }

  // Not in Farcaster Mini App (regular web): use router.canGoBack()
  // This is more accurate than pathHistory for regular web navigation
  return router.canGoBack();
};

/**
 * Determines if the back button should be shown based on navigation state and platform.
 * Handles special cases for Farcaster Mini Apps and access routes.
 */
const useEnableBackButton = () => {
  const router = useRouter();
  const pathname = useNavigationStore((state) => state.currentPath);
  const pathHistory = useNavigationStore((state) => state.pathHistory);
  const { isInMiniApp } = useFarcasterContext();

  // Special case: For access routes, check if the previous non-access route had a back button
  if (pathname.startsWith("/access")) {
    // Find the first non-access route in history
    for (let i = pathHistory.length - 1; i >= 0; i--) {
      const historyPath = pathHistory[i];
      if (historyPath && !historyPath.includes("/access/")) {
        return allowBackButton(historyPath);
      }
    }
    return false; // No previous route found, hide back button
  }

  // Early return: don't show back button if current route doesn't allow it
  if (!allowBackButton(pathname)) {
    return false;
  }

  // Native platforms: use pathHistory (router.canGoBack/canDismiss have timing
  // issues where nav state hasn't settled when TopBar re-renders)
  if (Platform.OS !== "web") {
    return pathHistory.length > 1;
  }

  // Web platform: handle Farcaster Mini App vs regular web
  return getWebBackButtonState(isInMiniApp, pathHistory, router);
};

const TopBar = () => {
  const { top } = useSafeAreaInsets();
  const pathname = useNavigationStore(
    (state: NavigationState) => state.currentPath,
  );
  const previousPathname = usePrevious(pathname);
  const segments = useNavigationStore(
    (state: NavigationState) => state.segments,
  );
  const previousSegments = usePrevious(segments);
  const screenName = useNavigationStore(
    (state: NavigationState) => state.screenName,
  );

  const options = useMemo(
    () =>
      getTopBarOptions(pathname, segments, previousPathname, previousSegments),
    [pathname, previousPathname, previousSegments, segments],
  );

  const height = useTopBarHeight();
  const showBackButton = useEnableBackButton();

  const animatedContainerStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(options.hidden ? 0 : 1, {
        duration: 250,
        easing: Easing.inOut(Easing.ease),
      }),
    };
  }, [options.hidden, height]);

  // if (options.hidden) return null;

  return (
    <LBox
      bg={options.bg ?? "transparent"}
      bottom={0}
      flexDirection="row"
      height={height}
      left={0}
      paddingBottom="s2"
      pb="s2"
      position="absolute"
      px="s4"
      right={0}
      style={[{ paddingTop: top }, animatedContainerStyle]}
      top={0}
      pointerEvents={options.hidden ? "none" : "auto"}
    >
      <LBox
        flex={1}
        flexDirection="row"
        alignItems="center"
        gap="s1"
        {...options.leftSide?.containerProps}
      >
        {options.leftSide?.buttons ? (
          options.leftSide?.buttons?.map((button, index) => (
            <Fragment key={`${button}-${index}`}>{BUTTONS[button]}</Fragment>
          ))
        ) : (
          <>{showBackButton ? <BackButton /> : null}</>
        )}
      </LBox>
      <LBox flex={2} {...options.middle?.containerProps}>
        {screenName ? (
          <PageTitle title={screenName} />
        ) : (
          options.middle?.component
        )}
      </LBox>
      <LBox
        flex={1}
        height="100%"
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap="s1"
        {...options.rightSide?.containerProps}
      >
        {options.rightSide?.buttons ? (
          options.rightSide?.buttons?.map((button, index) => (
            <Fragment key={`${button}-${index}`}>{BUTTONS[button]}</Fragment>
          ))
        ) : (
          <>{Platform.OS === "web" ? <ViewAppButton /> : <InboxButton />}</>
        )}
      </LBox>
    </LBox>
  );
};

export default TopBar;
